import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import logoTransparent from '../../assets/logo_transparent.png';

export default function TermsAndConditions() {
    const navigate = useNavigate();

    const handleBack = () => {
        navigate('/', { replace: true });
    };

    return (
        <div className="min-h-screen bg-[#2b0063] py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto bg-white shadow-2xl rounded-2xl overflow-hidden">
                {/* Header with Logo and Back Button */}
                <div className="absolute top-4 left-4 flex items-center space-x-4">
                    <button 
                        onClick={handleBack} 
                        className="flex items-center bg-transparent hover:bg-white/10 p-2 rounded-full transition-colors"
                    >
                        <ArrowLeft className="w-6 h-6 text-white mr-2" />
                        <span className="text-white font-semibold">Back</span>
                    </button>
                    
                </div>

                <div className="p-8 sm:p-12">
                    <div className="flex justify-between items-center mb-8 border-b-2 border-purple-200 pb-2">
                        <h1 className="text-4xl font-bold text-purple-900">
                            Terms and Conditions
                        </h1>
                        <img 
                            src={logoTransparent} 
                            alt="LastCall Logo" 
                            className="h-20 w-auto"
                        />
                    </div>

                    <p className="text-sm text-gray-600 mb-8 italic">
                        Last updated: January 28, 2025
                    </p>

                    <div className="p-6 bg-white shadow-md rounded-lg">
                        <h1 className="text-3xl font-bold mb-4">TERMS AND CONDITIONS</h1>
                        <p className="mb-4"><strong>Last updated: January 28, 2025</strong></p>

                        <h2 className="text-2xl font-semibold mb-2">AGREEMENT TO OUR LEGAL TERMS</h2>
                        <p>Welcome to LastCall! We are LastCall LLC, a company registered in Delaware.</p>
                        <p>We operate the website <a href="https://www.lastcallforbars.com/" className="text-purple-600 hover:underline">https://www.lastcallforbars.com/</a> (the "Site"), as well as any other related products and services that link to these legal terms (collectively, the "Services").</p>
                        <p>You can contact us at <a href="mailto:support@lastcallforbars.com" className="text-purple-600 hover:underline">support@lastcallforbars.com</a> for any questions or concerns.</p>
                        <p>These Terms and Conditions constitute a legally binding agreement between you and LastCall LLC regarding your access to and use of our Services. By using our Services, you acknowledge that you have read, understood, and agreed to these Terms. If you do not agree, you must discontinue use immediately.</p>
                        <p>We may update these Terms periodically. Any changes will be posted with an updated "Last updated" date. Your continued use of the Services after changes are posted signifies acceptance of those changes.</p>
                        <p>Our Services are intended for users who are at least 21 years old. If you are under 21, you are not permitted to use or register for the Services.</p>
                        <p>We recommend printing or saving a copy of these Terms for your records.</p>

                        <h2 className="text-2xl font-semibold mb-2">TABLE OF CONTENTS</h2>
                        <ol className="list-decimal list-inside mb-4">
                            <li>OUR SERVICES</li>
                            <li>INTELLECTUAL PROPERTY RIGHTS</li>
                            <li>USER REPRESENTATIONS</li>
                            <li>USER REGISTRATION</li>
                            <li>BAR ACCOUNTS & SMS MARKETING SERVICES</li>
                            <li>PAYMENT & SUBSCRIPTION TERMS</li>
                            <li>CANCELLATION & REFUNDS</li>
                            <li>PROHIBITED ACTIVITIES</li>
                            <li>USER-GENERATED CONTENT</li>
                            <li>THIRD-PARTY INTEGRATIONS</li>
                            <li>SERVICE MANAGEMENT</li>
                            <li>PRIVACY POLICY</li>
                            <li>TERMINATION OF SERVICES</li>
                            <li>DISPUTE RESOLUTION</li>
                            <li>LIMITATIONS OF LIABILITY</li>
                            <li>CONTACT US</li>
                        </ol>

                        <h2 className="text-2xl font-semibold mb-2">1. OUR SERVICES</h2>
                        <p>LastCall provides a SMS-based marketing platform that enables bars, nightclubs, and other venues to send promotional messages to their customers. Patrons join the platform by opting in via QR codes or other approved methods and may opt out at any time by following the unsubscribe instructions in the messages.</p>

                        <h2 className="text-2xl font-semibold mb-2">2. INTELLECTUAL PROPERTY RIGHTS</h2>
                        <p>All content, software, branding, and trademarks used in our Services are owned by or licensed to LastCall. You may not use, copy, or distribute any content without our express written consent.</p>

                        <h2 className="text-2xl font-semibold mb-2">3. USER REPRESENTATIONS</h2>
                        <p>By using the Services, you confirm that:</p>
                        <ul className="list-disc list-inside mb-4">
                            <li>You are at least 21 years old.</li>
                            <li>You have the legal authority to agree to these Terms.</li>
                            <li>You will use the Services in compliance with all applicable laws.</li>
                        </ul>

                        <h2 className="text-2xl font-semibold mb-2">4. USER REGISTRATION</h2>
                        <p>Bars and businesses using LastCall's platform must create an account. You agree to:</p>
                        <ul className="list-disc list-inside mb-4">
                            <li>Provide accurate, current, and complete information.</li>
                            <li>Maintain the confidentiality of your login credentials.</li>
                            <li>Be responsible for all activity under your account.</li>
                        </ul>

                        <h2 className="text-2xl font-semibold mb-2">5. BAR ACCOUNTS & SMS MARKETING SERVICES</h2>
                        <p>Bars and nightlife venues can send SMS promotions to customers who have opted in. LastCall ensures compliance with A2P 10DLC and TCPA regulations. Bars are responsible for ensuring they do not send spam or misleading messages.</p>

                        <h2 className="text-2xl font-semibold mb-2">6. PAYMENT & SUBSCRIPTION TERMS</h2>
                        <p>LastCall operates on a monthly subscription model with tiered pricing based on the number of patrons in a bar's database. Payments are processed automatically via the payment method on file. Failure to make timely payments may result in account suspension.</p>

                        <h2 className="text-2xl font-semibold mb-2">7. CANCELLATION & REFUNDS</h2>
                        <p>You may cancel your subscription at any time, but no refunds will be issued for the current billing cycle. Refund requests are evaluated on a case-by-case basis.</p>

                        <h2 className="text-2xl font-semibold mb-2">8. PROHIBITED ACTIVITIES</h2>
                        <p>You may not use LastCall's Services for:</p>
                        <ul className="list-disc list-inside mb-4">
                            <li>Spamming, harassment, or misleading promotions.</li>
                            <li>Illegal activities, including advertising promotions for unlicensed alcohol sales.</li>
                            <li>Collecting personal data without consent.</li>
                        </ul>
                        <p>Violation of these terms may result in immediate termination of your account.</p>

                        <h2 className="text-2xl font-semibold mb-2">9. USER-GENERATED CONTENT</h2>
                        <p>Bars and venues may upload content (e.g., promotional messages). By doing so, you grant LastCall a license to use, display, and distribute this content as needed to provide our Services.</p>

                        <h2 className="text-2xl font-semibold mb-2">10. THIRD-PARTY INTEGRATIONS</h2>
                        <p>Our platform may integrate with POS systems, reservation software, and third-party services. LastCall is not responsible for any issues arising from third-party services.</p>

                        <h2 className="text-2xl font-semibold mb-2">11. SERVICE MANAGEMENT</h2>
                        <p>We reserve the right to:</p>
                        <ul className="list-disc list-inside mb-4">
                            <li>Modify or discontinue the Services at any time.</li>
                            <li>Limit access to certain features or users at our discretion.</li>
                            <li>Take corrective action if a user violates these Terms.</li>
                        </ul>

                        <h2 className="text-2xl font-semibold mb-2">12. PRIVACY POLICY</h2>
                        <p>Your use of the Services is governed by our Privacy Policy, which outlines how we collect, store, and protect your data.</p>

                        <h2 className="text-2xl font-semibold mb-2">13. TERMINATION OF SERVICES</h2>
                        <p>We reserve the right to terminate your account without prior notice if you:</p>
                        <ul className="list-disc list-inside mb-4">
                            <li>Violate these Terms.</li>
                            <li>Fail to make required payments.</li>
                            <li>Engage in fraudulent or harmful activities.</li>
                        </ul>

                        <h2 className="text-2xl font-semibold mb-2">14. DISPUTE RESOLUTION</h2>
                        <p>All disputes will be resolved through binding arbitration under the rules of the American Arbitration Association (AAA). You waive any right to pursue class-action lawsuits against LastCall.</p>

                        <h2 className="text-2xl font-semibold mb-2">15. LIMITATIONS OF LIABILITY</h2>
                        <p>Users are solely responsible for providing accurate information when opting into our Services. LastCall does not independently verify user-submitted ages and is not liable for any 
                                false or misleading age representations. Bars and venues using LastCall's Services are responsible for ensuring compliance with all applicable age verification laws and regulations. 
                                LastCall disclaims any liability arising from a user's misrepresentation of their age.</p>

                        <br />

                        <p>To the fullest extent permitted by law:</p>
                        <ul className="list-disc list-inside mb-4">
                            <li>LastCall is not liable for any lost revenue, data breaches, or SMS delivery failures.</li>
                            <li>Our total liability will never exceed the total fees paid by you in the last 3 months.</li>
                        </ul>

                        <h2 className="text-2xl font-semibold mb-2">16. CONTACT US</h2>
                        <p>For any questions about these Terms, contact us at:</p>
                        <p>📧 <a href="mailto:support@lastcallforbars.com" className="text-purple-600 hover:underline">support@lastcallforbars.com</a></p>
                    </div>
                </div>
            </div>
        </div>
    );
}
